import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { ModalManager } from "@/modal-manager";
import { modalManagerToken } from "@/tokens";
import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
import { reviewsGalleryModalToken } from "@apps/reviews/tokens";
import { applyTransaction } from "@datorama/akita";
import { BehaviorSubject, Observable, Subscription, map, tap } from "rxjs";
import { inject, singleton } from "tsyringe";

import ReviewsGalleryQuery from "./queries/ReviewsGalleryQuery";
import ReviewsGalleryStore from "./stores/ReviewsGalleryStore";

type OpenReviewModal = {
    reviews$: Observable<ReviewEntity[]>;
    activeReviewUid: string;
    activeImageIndex?: number;
    widgetName?: REVIEWS_WIDGET_NAMES;
};

@singleton()
export default class ReviewsGalleryManager {
    public activeReview$ = this.reviewsGalleryQuery.active$;
    public activeImageIndex$ = new BehaviorSubject<number | undefined>(
        undefined
    );
    public isOpen$ = new BehaviorSubject<boolean>(false);
    public reviewsSubscription: Subscription | undefined = undefined;
    public widgetName$ = new BehaviorSubject<REVIEWS_WIDGET_NAMES | undefined>(
        undefined
    );

    constructor(
        private readonly reviewsGalleryStore: ReviewsGalleryStore,
        private readonly reviewsGalleryQuery: ReviewsGalleryQuery,
        @inject(modalManagerToken)
        private readonly modalManager: ModalManager
    ) {}

    openReviewModal({
        reviews$,
        activeReviewUid,
        activeImageIndex,
        widgetName,
    }: OpenReviewModal) {
        if (this.reviewsSubscription) {
            this.reviewsSubscription.unsubscribe();
        }

        this.reviewsSubscription = reviews$
            .pipe(
                tap((reviews) => {
                    this.reviewsGalleryStore.set(reviews);
                })
            )
            .subscribe();
        this.reviewsGalleryStore.setActive(activeReviewUid);
        this.isOpen$.next(true);
        this.widgetName$.next(widgetName);
        this.activeImageIndex$.next(activeImageIndex);
        this.modalManager.openModal(reviewsGalleryModalToken);
    }

    closeReviewModal() {
        this.modalManager.closeModal(reviewsGalleryModalToken);
        applyTransaction(() => {
            this.reviewsGalleryStore.update([]);
            this.reviewsGalleryStore.setActive(null);
        });
        this.isOpen$.next(false);

        if (this.reviewsSubscription) {
            this.reviewsSubscription.unsubscribe();
        }
    }

    next() {
        this.reviewsGalleryStore.setActive({ next: true });
    }

    prev() {
        this.reviewsGalleryStore.setActive({ prev: true });
    }

    selectReviewsCount() {
        return this.reviewsGalleryQuery.all$.pipe(
            map((reviews) => reviews.length)
        );
    }
}
